import { useState, useEffect, useCallback } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RefreshCw } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function UuidGenerator() {
  const [count, setCount] = useState(5);
  const [uuids, setUuids] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const generateUuids = useCallback(() => {
    const amount = Math.max(1, Math.min(1000, count || 1));
    const newUuids = [];
    for (let i = 0; i < amount; i++) {
      newUuids.push(uuidv4());
    }
    setUuids(newUuids);
  }, [count]);

  useEffect(() => {
    generateUuids();
  }, [generateUuids]);

  const handleCopy = () => {
    if (!uuids.length) return;
    copyToClipboard(uuids.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Online UUID Generator (v4)"
      description="Generate random v4 UUIDs (Universally Unique Identifiers) instantly with our free online tool. Perfect for database keys and unique ID generation."
      h1="UUID Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Set the number of UUIDs you need.",
        "Click 'Generate' to create your unique IDs.",
        "Copy all UUIDs at once with the copy button."
      ]}
      faq={[
        { question: "What is an online UUID generator and how does it work?", answer: "An online UUID generator is a utility that creates Universally Unique Identifiers (UUIDs) conforming to RFC 4122. It generates pseudo-random 128-bit values and formats them into five distinct hexadecimal groups separated by hyphens." },
        { question: "What is the difference between a UUID and a GUID?", answer: "Essentially, they are the same thing. UUID (Universally Unique Identifier) is the standard term defined by the IETF, whereas GUID (Globally Unique Identifier) is Microsoft's terminology for their implementation of the UUID standard." },
        { question: "How is a UUID Version 4 (v4) structured and formatted?", answer: "A UUID v4 consists of 32 hexadecimal characters grouped into five segments: 8-4-4-4-12 (e.g., f81d4fae-7dec-11d0-a765-00a0c91e6bf6). It always sets the version digit to '4' in the third segment, and the variant digit to '8', '9', 'a', or 'b' at the start of the fourth segment." },
        { question: "What is the mathematical probability of a UUID v4 collision?", answer: "The probability of a collision is virtually zero. With 122 bits of pure randomness, there are 2^122 (or 5.3 Undecillion) possible combinations. You would need to generate 1 billion UUIDs per second for 100 years for a 50% chance of a single collision." },
        { question: "How does UUID Version 4 differ from Version 1, 3, and 5?", answer: "UUID v1 is based on the host's MAC address and current timestamp. UUID v4 is based entirely on random numbers. UUID v3 (MD5) and UUID v5 (SHA-1) are deterministic, namespace-based name generators." },
        { question: "Are UUIDs safe to use as primary keys in database tables like PostgreSQL or MySQL?", answer: "Yes, UUIDs are excellent for distributed systems because primary keys can be generated offline without hitting the database. However, because they are non-sequential, standard indexing might lead to index fragmentation. Databases like PostgreSQL offer native UUID types to optimize storage." },
        { question: "Is this online UUID generator cryptographically secure?", answer: "Yes, our tool utilizes the Web Crypto API (`window.crypto.getRandomValues`) when available to generate random bytes, which is cryptographically secure and stronger than default Math.random() implementations." },
        { question: "Can you decode or reverse-engineer a UUID v4 to retrieve sensitive data?", answer: "No. Since a UUID v4 is made entirely of random values (excluding the version and variant bits), it contains no hidden metadata, machine MAC addresses, network node identities, or timestamp info. It is safe to expose publicly." },
        { question: "What are the advantages of using UUIDs over auto-incrementing integer IDs?", answer: "Auto-incrementing integer IDs are predictable, exposing your business metrics (e.g., transaction count or user signups) and opening systems to enumeration attacks. UUIDs are completely unpredictable, decentralized, and do not suffer from multi-master database collisions." },
        { question: "Does this online UUID generator operate offline?", answer: "Yes, this UUID generator runs entirely in your local browser using client-side JavaScript. No UUIDs are transmitted or logged on external servers, ensuring complete security and data privacy." }
      ]}
    >
      <div className="space-y-6 max-w-2xl mx-auto">
        <div className="flex items-end gap-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <div className="space-y-2">
            <Label>Number of UUIDs</Label>
            <Input 
              type="number" 
              min="1" 
              max="1000" 
              value={count} 
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-32 bg-white"
            />
          </div>
          
          <Button onClick={generateUuids} className="ml-auto">
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-medium text-slate-700">Generated UUIDs (v4)</Label>
            <span className="text-xs text-slate-500">{uuids.length} generated</span>
          </div>
          <Textarea
            value={uuids.join("\n")}
            readOnly
            className="min-h-[300px] font-mono leading-loose bg-slate-50"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" className="gap-2 w-full">
            <Copy className="h-4 w-4" />
            {copied ? "Copied!" : "Copy All"}
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
